/** Formatting helpers matching the Asia Wallet design (thin-space grouping). */

const NBSP = ' '; // narrow no-break space used for digit grouping in the design

export function groupInt(n: number): string {
  return Math.trunc(Math.abs(n))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Split a USD amount into the big integer part and the ".xx" fraction so the
 *  balance can render the cents smaller, as in the Figma. */
export function splitAmount(n: number): { int: string; frac: string; sign: string } {
  const sign = n < 0 ? '-' : '';
  // Round to whole cents first so e.g. 0.999 → 1.00 (carry), not 0.100.
  const cents = Math.round(Math.abs(n) * 100);
  const int = groupInt(Math.floor(cents / 100));
  const frac = (cents % 100).toString().padStart(2, '0');
  return { int, frac, sign };
}

export function fmtUsd(n: number, opts: { sign?: boolean } = {}): string {
  const s = n < 0 ? '-' : opts.sign ? '+' : '';
  // Round to whole cents first so e.g. 0.999 → $1.00 (carry), not $0.100.
  const cents = Math.round(Math.abs(n) * 100);
  const int = groupInt(Math.floor(cents / 100));
  return `${s}$${int}.${(cents % 100).toString().padStart(2, '0')}`;
}

/** Coin unit price. Truncated (not rounded) so a stablecoin at 0.999 shows $0.99
 *  like Binance rather than rounding up to $1.00. Pass `decimals` for a fixed
 *  precision per coin (USDT 2 → $0.99, TRX 4 → $0.3223); otherwise it auto-picks
 *  2 for normal prices and more digits for tiny ones so they don't hit $0.00. */
export function fmtPrice(n: number, decimals?: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const d = decimals ?? (abs === 0 || abs >= 0.01 ? 2 : 6);
  const factor = 10 ** d;
  const truncated = Math.floor(abs * factor) / factor;
  const [int = '0', frac = ''] = truncated.toFixed(d).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${sign}$${grouped}.${frac}`;
}

/** Display decimals for a coin's unit price (USDT 2, TRX 4). */
export function priceDecimals(symbol: string): number {
  return symbol === 'TRX' ? 4 : 2;
}

export function fmtCoin(n: number, symbol: string): string {
  const abs = Math.abs(n);
  const s = n < 0 ? '-' : n > 0 ? '+' : '';
  const body = abs >= 1 ? groupInt(abs) : abs.toString();
  return `${s}${body}${NBSP}${symbol}`;
}

export function fmtPct(frac: number): string {
  const s = frac >= 0 ? '+' : '';
  // Always 2 decimals — e.g. "+2.10%", "-0.03%", "+0.00%".
  return `${s}${(frac * 100).toFixed(2)}%`;
}

export function shortAddr(addr: string, head = 4, tail = 4): string {
  if (addr.length <= head + tail) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

export function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function fmtDateHeading(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "23.06.2026" — dd.mm.yyyy, as in the transaction-detail sheet (Figma 2:8394). */
export function fmtDateDMY(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}
